import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import some from 'lodash/fp/some';
import { AppState } from '.';
import {
  AuthConfig,
  exceptionOf,
  Role,
  SerializedException,
  Token,
  User,
} from '../models';
import { AuthService, KeycloakService } from '../services';
import { rmAuthorizationHeader, setAuthorizationHeader } from '../services/axios';

const TOKEN = 'k1.login';

export const AUTH_FEATURE_KEY = 'auth';

interface AuthState {
  user: User | null;
  token: Token | null;
  loading: boolean;
  errors: SerializedException[];
  // whether perform doLogin async action.
  didLogin: boolean;
  submitting: boolean;
}

interface LoginProps {
  authConfig: AuthConfig;
  rememberMe: boolean;
}

export const createInitialState = (): AuthState => ({
  token: null,
  user: null,
  loading: false,
  errors: [],
  didLogin: false,
  submitting: false,
});

export const doLogin = createAsyncThunk(
  'auth/login',
  async ({ authConfig, rememberMe }: LoginProps, { rejectWithValue }) => {
    try {
      // Login by Keycloak service instead
      const token = await KeycloakService.getToken({
        redirectUrl: authConfig.redirectUrl,
      });

      if (!token) {
        rmAuthorizationHeader();
        localStorage.removeItem(TOKEN);
        return rejectWithValue(exceptionOf(Error('Cannot initialize Keycloak')).toJson());
      }

      const { jwt, refreshJwt } = token;

      setAuthorizationHeader(jwt);

      const isInIframe = window.self !== window.top;

      if (rememberMe && !isInIframe) {
        localStorage.setItem(TOKEN, JSON.stringify({
          jwt,
          refreshJwt,
        }));
      }
      const user = await AuthService.me();

      /* eslint-disable no-underscore-dangle */
      if ((window as any)._paq) {
        (window as any)._paq.push(['setUserId', user.email]);
      }

      return {
        user,
        token: {
          jwt,
          refreshJwt,
          user,
        },
      };
    } catch (e: any) {
      rmAuthorizationHeader();
      localStorage.removeItem(TOKEN);
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doResume = createAsyncThunk(
  'auth/resume',
  async (token: Partial<Pick<Token, 'refreshJwt'>> & Required<Pick<Token, 'jwt'>> | undefined, { rejectWithValue }) => {
    try {
      const userToken = token ?? (JSON.parse(localStorage.getItem(TOKEN) || 'null') as Token);
      setAuthorizationHeader(userToken.jwt);
      const user = await AuthService.me();

      const tokenAfterGetMe = JSON.parse(localStorage.getItem(TOKEN) || 'null') as Token;

      // If user is authenticated by refresh token before get me success, keep
      // the refreshed token pair but always take the user just returned by
      // /auth/me — the cached copy can be stale (e.g. avatar changed in TestOps).
      if (tokenAfterGetMe?.user && !token) {
        const refreshedToken = { ...tokenAfterGetMe, user };
        localStorage.setItem(TOKEN, JSON.stringify(refreshedToken));
        return { user, token: refreshedToken };
      }

      localStorage.setItem(TOKEN, JSON.stringify({
        jwt: userToken.jwt,
        refreshJwt: userToken.refreshJwt,
        user,
      }));
      // update matomo userId
      /* eslint-disable no-underscore-dangle */
      if ((window as any)._paq) {
        (window as any)._paq.push(['setUserId', user.email]);
      }
      /* eslint-enable */
      localStorage.setItem('ajs_user_id', `"${user.email}"`);
      // eslint-disable-next-line no-underscore-dangle
      window._sva = window._sva || {};
      // eslint-disable-next-line no-underscore-dangle
      window._sva.traits = {
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
      };
      return { user, token: { jwt: userToken.jwt, refreshJwt: userToken.refreshJwt, user } };
    } catch (e: any) {
      rmAuthorizationHeader();
      localStorage.removeItem(TOKEN);
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doResumeWithOnlyAccessToken = createAsyncThunk(
  'auth/doResumeWithOnlyAccessToken',
  async (token: Partial<Pick<Token, 'refreshJwt'>> & Required<Pick<Token, 'jwt'>> | undefined, { rejectWithValue }) => {
    try {
      if (!token) return undefined;

      setAuthorizationHeader(token.jwt);

      const user = await AuthService.me();

      return { user, token: { jwt: token.jwt, refreshJwt: token.refreshJwt, user } };
    } catch (e: any) {
      rmAuthorizationHeader();
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

/**
 * Resolves `true` when Keycloak has taken over the browser (logout redirect issued),
 * `false` when there was no SSO session to terminate. Local auth state is cleared either way.
 */
export const doLogout = createAsyncThunk(
  'auth/logout',
  async (authConfig: AuthConfig): Promise<boolean> => {
    try {
      return await KeycloakService.logout(authConfig);
    } finally {
      rmAuthorizationHeader();
      localStorage.removeItem(TOKEN);
      // Clear survicate data
      // eslint-disable-next-line no-underscore-dangle
      window._sva = window._sva || {};
      // eslint-disable-next-line no-underscore-dangle
      if (window._sva.destroyVisitor) {
        // eslint-disable-next-line no-underscore-dangle
        window._sva.destroyVisitor();
      }
    }
  },
);

export const doRefreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const token = await KeycloakService.checkAndRenewToken();
      if (!token?.jwt) {
        throw new Error('[Auth] Refresh-token renewal returned no access token');
      }
      return {
        token: {
          jwt: token.jwt,
          refreshJwt: token.refreshJwt,
          user: undefined,
        },
        user: undefined,
      };
    } catch (e: any) {
      rmAuthorizationHeader();
      localStorage.removeItem(TOKEN);
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const authSlice = createSlice({
  name: AUTH_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doLogin.pending, state => {
      state.loading = true;
    });
    builder.addCase(doLogin.fulfilled, (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.loading = false;
      state.errors = [];
      state.didLogin = true;
    });
    builder.addCase(doLogin.rejected, (state, action) => {
      const payload = action.payload as SerializedException;
      state.user = null;
      state.token = null;
      state.loading = false;
      state.errors.push(payload);
    });

    builder.addCase(doResume.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(doResume.fulfilled, (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.loading = false;
      state.didLogin = true;
    });
    builder.addCase(doResume.rejected, (state, action) => {
      const payload = action.payload as SerializedException;
      state.user = null;
      state.token = null;
      state.loading = false;
      state.errors.push(payload);
    });

    builder.addCase(doResumeWithOnlyAccessToken.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(doResumeWithOnlyAccessToken.fulfilled, (state, action) => {
      if (action.payload) {
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.loading = false;
        state.didLogin = true;
      }
    });
    builder.addCase(doResumeWithOnlyAccessToken.rejected, (state, action) => {
      const payload = action.payload as SerializedException;
      state.user = null;
      state.token = null;
      state.loading = false;
      state.errors.push(payload);
    });

    builder.addCase(doLogout.pending, state => {
      state.loading = true;
    });
    builder.addCase(doLogout.fulfilled, state => {
      state.user = null;
      state.token = null;
      state.loading = false;
    });
    builder.addCase(doRefreshToken.pending, state => {
      state.loading = true;
    });
    builder.addCase(doRefreshToken.fulfilled, (state, action) => {
      // @ts-ignore
      state.token = action.payload.token;
      state.loading = false;
      state.errors = [];
      state.didLogin = true;
    });
    builder.addCase(doRefreshToken.rejected, (state, action) => {
      const payload = action.payload as SerializedException;
      state.user = null;
      state.token = null;
      state.loading = false;
      state.didLogin = false;
      state.errors.push(payload);
    });
  },
});

const selectAuthFeature = (state: AppState) => state[AUTH_FEATURE_KEY];

export const selectLoading = createSelector(
  selectAuthFeature,
  authState => authState.loading,
);
export const selectUser = createSelector(
  selectAuthFeature,
  authState => authState.user,
);
export const selectToken = createSelector(
  selectAuthFeature,
  authState => authState.token,
);
export const selectIsAuthenticated = createSelector(
  selectAuthFeature,
  authState => !!(authState.user && authState.token) || !!localStorage.getItem(TOKEN),
);
export const selectIsRootOrAdmin = createSelector(
  selectAuthFeature,
  authState => some(role => [Role.ADMIN, Role.ROOT].includes(role), authState.user?.roles),
);
export default authSlice.reducer;
